import { forwardRef, useRef, useState } from "react";

import { ArrowLeftTailIcon, ArrowUpTailIcon, CloseIcon } from "@/assets/icons";
import { MESSAGE } from "@/constants/message";

const { DISLIKE_POPUP } = MESSAGE.AI_SUMMARY_FEEDBACK;

const DISLIKE_OPTIONS = [
  { label: DISLIKE_POPUP.OPTION_MISLEADING, value: "misleading" },
  { label: DISLIKE_POPUP.OPTION_INCOMPLETE, value: "incomplete" },
  { label: DISLIKE_POPUP.OPTION_IRRELEVANT, value: "irrelevant" },
  { label: DISLIKE_POPUP.OPTION_UNCLEAR, value: "unclear" }
];

interface DislikeFeedbackPopupProps {
  isSubmitting: boolean;
  onSubmit: (reason?: string, comment?: string) => void;
  onClose: () => void;
}

const DislikeFeedbackPopup = forwardRef<
  HTMLDivElement,
  DislikeFeedbackPopupProps
>(({ isSubmitting, onSubmit, onClose }, ref) => {
  const [view, setView] = useState<"options" | "other">("options");
  const [text, setText] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleOtherClick = () => {
    setView("other");

    requestAnimationFrame(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      textarea.scrollIntoView({ behavior: "smooth", block: "center" });
      textarea.focus({ preventScroll: true });
    });
  };

  return (
    <div
      ref={ref}
      className="relative z-20 mt-3 w-full rounded-2xl border border-neutral-200 bg-white shadow-sm"
      aria-label={DISLIKE_POPUP.TITLE}
    >
      <div className="flex items-start justify-between gap-2 px-4 pb-3 pt-4">
        <div className="flex items-center gap-2">
          {view === "other" && (
            <button
              type="button"
              onClick={() => setView("options")}
              className="shrink-0 text-neutral-600 hover:text-neutral-900"
              aria-label={DISLIKE_POPUP.BACK_TO_OPTIONS}
            >
              <ArrowLeftTailIcon />
            </button>
          )}
          <span className="text-base font-semibold text-black">
            {DISLIKE_POPUP.TITLE}
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="mt-0.5 shrink-0 hover:opacity-70"
          aria-label={DISLIKE_POPUP.CLOSE}
        >
          <CloseIcon />
        </button>
      </div>

      {view === "options" && (
        <div className="flex flex-wrap gap-2 px-4 pb-4">
          {DISLIKE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              disabled={isSubmitting}
              onClick={() => onSubmit(option.value)}
              className="rounded-xl border border-primary-teal px-3 py-1.5 text-sm font-medium text-primary-teal hover:bg-[#E6F6EF] disabled:opacity-50"
            >
              {option.label}
            </button>
          ))}
          <button
            type="button"
            disabled={isSubmitting}
            onClick={handleOtherClick}
            className="rounded-xl border border-primary-teal px-3 py-1.5 text-sm font-medium text-primary-teal hover:bg-[#E6F6EF] disabled:opacity-50"
          >
            Other...
          </button>
        </div>
      )}

      {view === "other" && (
        <div className="px-4 pb-4">
          <div className="relative rounded-xl border border-neutral-200 bg-white p-3 focus-within:border-primary-teal">
            <textarea
              ref={textareaRef}
              value={text}
              onChange={(event) => setText(event.target.value)}
              placeholder={DISLIKE_POPUP.PLACEHOLDER}
              rows={4}
              className="w-full resize-none bg-transparent text-xs text-black placeholder:text-neutral-400 focus:outline-none"
            />
            <div className="flex justify-end">
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => onSubmit("other", text.trim() || undefined)}
                aria-label={DISLIKE_POPUP.SUBMIT}
                className="flex h-8 w-8 items-center justify-center rounded-lg bg-neutral-300 text-white hover:bg-primary-teal disabled:opacity-50"
              >
                <ArrowUpTailIcon className="h-4 w-4 fill-current" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

DislikeFeedbackPopup.displayName = "DislikeFeedbackPopup";

export default DislikeFeedbackPopup;
